import { InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useCallback } from 'react';
import { isMobile } from 'react-device-detect';

import type { UserInfo } from '@/components/remote-software/types/index.types';

import { CLIENT_ID, ECM_SCOPES } from '@/config/EnviromentConfig';
import msalInstance, { getAuthCache } from '@/config/MsalAuthConfig';
import { account } from '@/utils';

// Type for extended ID token claims
interface ExtendedIdTokenClaims {
  endCustomerId?: string;
  sub?: string;
  email?: string;
  'signInNames.emailAddress'?: string;
  emails?: string[];
  aud?: string;
}

export interface UseMsalHelperResult {
  /**
   * Get fresh UserInfo from MSAL on-demand.
   * Returns user info with null values when not authenticated.
   * Now async to support reliable token acquisition on mobile.
   */
  getUserInfo: () => Promise<UserInfo>;

  /**
   * Get the active MSAL account if available.
   */
  getActiveAccount: () => AccountInfo | null;

  /**
   * Check if user is currently authenticated.
   */
  isAuthenticated: boolean;

  /**
   * Access to the raw MSAL instance for advanced use cases.
   */
  msalInstance: ReturnType<typeof useMsal>['instance'];
}

/**
 * Custom hook that provides centralized MSAL utilities for extracting user information.
 *
 * This hook ensures fresh data is always retrieved directly from MSAL,
 * avoiding stale cached values that can occur in native webview environments.
 *
 * @returns Object containing getUserInfo callback and other MSAL utilities
 *
 * @example
 * ```typescript
 * const { getUserInfo, isAuthenticated } = useMsalHelper();
 *
 * // Get fresh user info when needed
 * const userInfo = getUserInfo();
 *
 * // Check auth status
 * if (isAuthenticated) {
 * }
 * ```
 */
const useMsalHelper = (): UseMsalHelperResult => {
  // const { isNative } = useContext(EnvironmentContext);

  /**
   * Extract UserInfo directly from a JWT idToken.
   * Used as fallback when MSAL account object is unavailable on mobile.
   */
  const extractUserInfoFromIdToken = useCallback((idToken: string): UserInfo | null => {
    try {
      // JWT format: header.payload.signature
      const parts = idToken.split('.');
      if (parts.length !== 3) {
        console.error('[useMsalHelper] Invalid JWT format', {
          timestamp: new Date().toISOString(),
          partsCount: parts.length,
        });
        return null;
      }

      // Decode base64 payload
      const payload = JSON.parse(atob(parts[1]));

      // Extract UserInfo from token claims
      const userInfo: UserInfo = {
        endCustomerId: payload.endCustomerId || null,
        b2cId: payload.sub || null, // sub is the Azure B2C user ID
        email: payload.email || payload['signInNames.emailAddress'] || payload.emails?.[0] || null,
      };

      return userInfo;
    } catch (error) {
      console.error('[useMsalHelper] Failed to decode idToken', {
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : String(error),
      });
      return null;
    }
  }, []);

  /**
   * Acquire tokens silently for native platform access.
   * This refreshes tokens in webview storage, making them accessible to native apps.
   * The token acquisition itself is the workaround for iOS/Android webview storage isolation.
   */
  const acquireTokensForNative = useCallback(async (account: AccountInfo, platform: 'ios' | 'android') => {
    try {
      const response = await msalInstance.acquireTokenSilent({
        account,
        scopes: ECM_SCOPES,
      });

      return response;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.error('[useMsalHelper] acquireTokensForNative: Interaction required', {
          timestamp: new Date().toISOString(),
          platform,
          error: error.message,
        });
      } else {
        console.error('[useMsalHelper] acquireTokensForNative: Token acquisition failed', {
          timestamp: new Date().toISOString(),
          platform,
          error: error instanceof Error ? error.message : String(error),
        });
      }
      throw error;
    }
  }, []);

  /**
   * Get fresh UserInfo directly from MSAL on-demand.
   * This bypasses AccountContext and ensures we always have the latest data.
   *
   * Primary method: Attempts token acquisition first (most reliable on mobile).
   * Falls back to account-based extraction if token acquisition fails.
   */
  const getUserInfo = useCallback(async (): Promise<UserInfo> => {
    // Primary method: Try token acquisition first (works reliably even when account APIs fail on mobile)
    try {
      let activeAccount = msalInstance.getActiveAccount();

      // If no active account, try getAllAccounts
      if (!activeAccount) {
        const allAccounts = msalInstance.getAllAccounts();
        const applicationAccounts = allAccounts.filter((a) => a.idTokenClaims && a.idTokenClaims.aud === CLIENT_ID);
        if (applicationAccounts.length > 0) {
          activeAccount = applicationAccounts[0];
        }
      }

      // If we have an account, try token acquisition
      if (activeAccount) {
        const tokenRequest = {
          account: activeAccount,
          scopes: ECM_SCOPES,
        };

        const { idTokenClaims } = await msalInstance.acquireTokenSilent(tokenRequest);

        // Extract UserInfo from fresh token claims
        const claims = idTokenClaims as ExtendedIdTokenClaims;
        const userInfo: UserInfo = {
          endCustomerId: claims?.endCustomerId || null,
          b2cId: claims?.sub || null,
          email: claims?.['signInNames.emailAddress'] || claims?.emails?.[0] || claims?.email || null,
        };

        // Also acquire tokens for native platform if needed
        if (isMobile) {
          const platform = window.webkit?.messageHandlers?.appInterface ? 'ios' : window.androidApp ? 'android' : null;
          if (platform) {
            acquireTokensForNative(activeAccount, platform).catch((error) => {
              console.error('[useMsalHelper] getUserInfo: Failed to acquire tokens for native', {
                timestamp: new Date().toISOString(),
                platform,
                error: error instanceof Error ? error.message : String(error),
              });
            });
          }
        }

        return userInfo;
      }
    } catch (error) {
      console.warn('[useMsalHelper] getUserInfo: Token acquisition failed, trying fallbacks', {
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : String(error),
      });
    }

    // Fallback: Use existing account-based logic
    let activeAccount = msalInstance.getActiveAccount();

    // Defensive fallback for native environments where getActiveAccount() might return null
    // due to iOS localStorage edge cases, even after successful authentication
    if (!activeAccount && isMobile) {
      const allAccounts = msalInstance.getAllAccounts();
      const applicationAccounts = allAccounts.filter((a) => a.idTokenClaims && a.idTokenClaims.aud === CLIENT_ID);

      if (applicationAccounts.length > 0) {
        activeAccount = applicationAccounts[0];
      }
    }

    // Third fallback: extract UserInfo directly from cached idToken
    // This works even when MSAL localStorage completely fails on mobile
    if (!activeAccount && isMobile) {
      const authCache = getAuthCache();

      if (authCache?.idToken) {
        const userInfoFromToken = extractUserInfoFromIdToken(authCache.idToken);

        if (userInfoFromToken) {
          return userInfoFromToken;
        }
      }

      // If we have the account in cache, try using it
      if (authCache?.account) {
        activeAccount = authCache.account;
      }
    }

    if (!activeAccount) {
      // Not authenticated - return all null
      return {
        endCustomerId: null,
        b2cId: null,
        email: null,
      };
    }

    // Note: Token acquisition for native is handled in primary path above
    // If we're in this fallback, it means token acquisition already failed

    // Extract values from MSAL token claims
    const b2cId = account.retrieveB2cId(activeAccount);
    const email = account.retrieveEmail(activeAccount);
    const endCustomerId = account.retrieveEndCustomerId(activeAccount);

    const userInfo: UserInfo = {
      endCustomerId: endCustomerId || null,
      b2cId: b2cId || null,
      email: email || null,
    };

    return userInfo;
  }, [acquireTokensForNative, extractUserInfoFromIdToken]);

  /**
   * Get the active MSAL account if available
   */
  const getActiveAccount = useCallback((): AccountInfo | null => {
    return msalInstance.getActiveAccount();
  }, []);

  /**
   * Check if user is currently authenticated
   */
  const isAuthenticated = !!msalInstance.getActiveAccount();

  return {
    getUserInfo,
    getActiveAccount,
    isAuthenticated,
    msalInstance: msalInstance,
  };
};

export default useMsalHelper;
